import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Card, Grid, Icon, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { get, post } from "utils/WebService";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import { toCurrency } from "utils/NumberUtils";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";

const escaparHtml = (texto) => String(texto ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const pad = (n) => String(n).padStart(2, "0");
const ontem = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Baixa o .zip que o backend manda em base64 (pasta da PC não configurada). */
const baixarZip = (base64, nome) => {
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/zip" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nome || "Prestação de Contas UMC.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

/**
 * Prestação de Contas no modelo da UMC (cliente com o modelo UMC em Ferramentas > Clientes): por data de pagamento,
 * a planilha da PC e o arquivo de retorno da UMC de cada carteira com pagamento no dia. Prévia sem gravar; "Gerar"
 * salva na pasta da Prestação de Contas (sem pasta, baixa um .zip). Histórico das gerações da tela e automáticas.
 */
export default function PrestacaoContasUmc({ cliente, seletorCliente }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);

    const [data, setData] = useState(ontem());
    const [buscando, setBuscando] = useState(false);
    const [gerando, setGerando] = useState(false);
    const [previa, setPrevia] = useState(null);
    const [gerada, setGerada] = useState(null);
    const [historico, setHistorico] = useState([]);

    const idCliente = cliente?.id || cliente?.cvId;

    const carregarHistorico = useCallback(() => {
        if (!idCliente) return;
        get(`/relatorios/prestacao-contas/umc/historico?cliente=${encodeURIComponent(idCliente)}`)
            .then(res => setHistorico(Array.isArray(res) ? res : []))
            .catch(() => setHistorico([]));
    }, [idCliente]);

    useEffect(() => {
        setPrevia(null);
        setGerada(null);
        carregarHistorico();
    }, [idCliente, carregarHistorico]);

    useEffect(() => {
        setPrevia(null);
        setGerada(null);
    }, [data]);

    const verPrevia = () => {
        if (!data) {
            errorAlert("Escolha a data do pagamento.");
            return;
        }
        setBuscando(true);
        setGerada(null);
        get(`/relatorios/prestacao-contas/umc/previa?cliente=${encodeURIComponent(idCliente)}&data=${data}`)
            .then(res => setPrevia(res))
            .catch(err => errorAlert(err?.message || "Erro ao montar a prévia da Prestação de Contas."))
            .finally(() => setBuscando(false));
    };

    const carteiras = previa?.carteiras || [];
    const totalRecebido = carteiras.reduce((s, c) => s + Number(c.recebido || 0), 0);
    const jaGeradas = carteiras.filter(c => c.jaGerada);

    const gerar = () => {
        const destino = previa.salvarEm
            ? `Os arquivos serão salvos em <b>${escaparHtml(previa.salvarEm)}</b> (não são baixados no computador).`
            : "A pasta da Prestação de Contas não está configurada: os arquivos serão baixados num .zip.";
        const repetida = jaGeradas.length
            ? `<br/><br/>Já há PC gerada desta data em ${jaGeradas.map(c => escaparHtml(c.carteira)).join(", ")}: os arquivos novos ganham " (2)" no nome.`
            : "";
        infoAviso(
            `Gerar a Prestação de Contas de <b>${escaparHtml(previa.cliente)}</b> dos pagamentos de <b>${escaparHtml(previa.data)}</b> `
            + `(${carteiras.length} carteira(s), <b>${toCurrency(totalRecebido)}</b> para a UMC)?<br/><br/>${destino}${repetida}`,
            () => {
                setGerando(true);
                post("/relatorios/prestacao-contas/umc/gerar", { cliente: idCliente, data })
                    .then(res => {
                        if (res?.zip) baixarZip(res.zip, res.nomeZip);
                        setGerada(res);
                        successAlert(res?.salvoNaPasta
                            ? "Prestação de Contas gerada e salva na pasta."
                            : "Prestação de Contas gerada. Os arquivos foram baixados (pasta não configurada).");
                        carregarHistorico();
                    })
                    .catch(err => errorAlert(err?.message || "Erro ao gerar a Prestação de Contas."))
                    .finally(() => setGerando(false));
            }
        );
    };

    const celulaCab = { fontWeight: "bold", fontSize: "0.75rem", backgroundColor: cores.painel, color: cores.texto, whiteSpace: "nowrap", py: 1 };
    const celula = { fontSize: "0.8rem", color: cores.texto, verticalAlign: "top", py: 1 };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Card sx={{ px: 2, py: 1.5 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        {seletorCliente}
                        <Grid item xs={12} sm={6} md={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data do pagamento</InputLabel>
                            <MDInput type="date" size="small" fullWidth value={data} onChange={(e) => setData(e.target.value)}
                                inputProps={{ max: ontem() }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>&nbsp;</InputLabel>
                            <MDButton variant="contained" color="info" size="medium" fullWidth sx={{ height: 38 }}
                                onClick={verPrevia} disabled={buscando || gerando}>
                                <Icon sx={{ mr: 1 }}>search</Icon>{buscando ? "Buscando..." : "Ver dados"}
                            </MDButton>
                        </Grid>
                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="text" sx={{ px: 1 }}>
                                Modelo da UMC: por data de pagamento, a planilha da PC (acordos, títulos negociados e liquidações) e o
                                arquivo de retorno da UMC de cada carteira com pagamento no dia, sem os honorários do escritório.
                            </MDTypography>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>

            {previa && (
                <Grid item xs={12}>
                    <Card sx={{ p: 2 }}>
                        <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={2} mb={1.5}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"} display="block">
                                    {previa.cliente} · pagamentos de {previa.data}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    {carteiras.length === 0
                                        ? "Nenhum pagamento de parcela de acordo nesta data."
                                        : <>Para a UMC: <b>{toCurrency(totalRecebido)}</b> em {carteiras.reduce((s, c) => s + c.pagamentos, 0)} pagamento(s)</>}
                                </MDTypography>
                            </MDBox>
                            <MDButton variant="contained" color="info" size="medium"
                                onClick={gerar} disabled={gerando || !!gerada || carteiras.length === 0}>
                                <Icon sx={{ mr: 1 }}>{gerada ? "check" : "description"}</Icon>
                                {gerando ? "Gerando..." : gerada ? "PC gerada" : "Gerar planilha e arquivo de retorno"}
                            </MDButton>
                        </MDBox>

                        {gerada ? (
                            <Alert severity="success" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                                {(gerada.carteiras || []).map(c => (
                                    <MDBox key={c.carteira} sx={{ color: "inherit" }}>
                                        <b>{c.carteira}</b>: {c.planilha} · {c.retorno}
                                    </MDBox>
                                ))}
                                {!gerada.salvoNaPasta && <MDBox sx={{ color: "inherit" }}>Baixados no .zip (a pasta da Prestação de Contas não está configurada).</MDBox>}
                            </Alert>
                        ) : previa.salvarEm ? (
                            <Alert severity="info" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                                Ao gerar, os arquivos serão salvos em <b>{previa.salvarEm}</b> (não são baixados no computador).
                            </Alert>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.8rem" }}>
                                A pasta da Prestação de Contas não está configurada (Parametrização do Sistema &gt; Pastas e
                                Arquivos): ao gerar, os arquivos serão baixados num .zip, e a geração automática não funciona.
                            </Alert>
                        )}

                        {carteiras.length > 0 && (
                            <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1 }}>
                                <Table size="small">
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            <TableCell sx={celulaCab}>Carteira</TableCell>
                                            <TableCell sx={celulaCab} align="right">Acordos novos</TableCell>
                                            <TableCell sx={celulaCab} align="right">Parcelas</TableCell>
                                            <TableCell sx={celulaCab} align="right">Títulos negociados</TableCell>
                                            <TableCell sx={celulaCab} align="right">Pagamentos</TableCell>
                                            <TableCell sx={celulaCab} align="right">Para a UMC</TableCell>
                                            <TableCell sx={celulaCab} align="right">Honorários (fora)</TableCell>
                                            <TableCell sx={celulaCab}>Já gerada</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {carteiras.map(c => (
                                            <TableRow key={c.carteira}>
                                                <TableCell sx={celula}>
                                                    {c.carteira}
                                                    {(c.avisos || []).map((a, i) => (
                                                        <MDTypography key={i} variant="caption" display="block" color="warning" sx={{ maxWidth: 520 }}>
                                                            {a}
                                                        </MDTypography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={celula} align="right">{c.acordos}</TableCell>
                                                <TableCell sx={celula} align="right">{c.parcelas}</TableCell>
                                                <TableCell sx={celula} align="right">{c.titulos}</TableCell>
                                                <TableCell sx={celula} align="right">{c.pagamentos}</TableCell>
                                                <TableCell sx={celula} align="right">{toCurrency(c.recebido)}</TableCell>
                                                <TableCell sx={celula} align="right">{toCurrency(c.honorarios)}</TableCell>
                                                <TableCell sx={celula}>{c.jaGerada ? `${c.jaGerada.em} · ${c.jaGerada.por}` : "-"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                </Grid>
            )}

            <Grid item xs={12}>
                <Card sx={{ p: 2 }}>
                    <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, maxHeight: 420 }}>
                        <Table size="small" stickyHeader>
                            <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow>
                                    <TableCell sx={celulaCab}>Pagamentos de</TableCell>
                                    <TableCell sx={celulaCab}>Carteira</TableCell>
                                    <TableCell sx={celulaCab}>Gerada em</TableCell>
                                    <TableCell sx={celulaCab}>Por</TableCell>
                                    <TableCell sx={celulaCab} align="right">Pagamentos</TableCell>
                                    <TableCell sx={celulaCab} align="right">Para a UMC</TableCell>
                                    <TableCell sx={celulaCab}>Arquivos</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historico.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ ...celula, py: 2 }}>Nenhuma Prestação de Contas gerada para este cliente.</TableCell>
                                    </TableRow>
                                )}
                                {historico.map((h, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell sx={celula}>{h.data}</TableCell>
                                        <TableCell sx={celula}>{h.carteira}</TableCell>
                                        <TableCell sx={celula}>{h.geradoEm}</TableCell>
                                        <TableCell sx={celula}>{h.por}</TableCell>
                                        <TableCell sx={celula} align="right">{h.pagamentos ?? "-"}</TableCell>
                                        <TableCell sx={celula} align="right">{h.recebido != null ? toCurrency(h.recebido) : "-"}</TableCell>
                                        <TableCell sx={{ ...celula, wordBreak: "break-all" }}>
                                            {h.situacao === "ERRO"
                                                ? <MDTypography variant="caption" color="error">Erro: {h.erro}</MDTypography>
                                                : <>{h.planilha}<br />{h.retorno}</>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <MDTypography variant="caption" color="text" display="block" mt={1}>
                        As últimas 60 gerações deste cliente, da tela e automáticas (todo dia útil às 7h, com o cliente marcado em
                        Ferramentas &gt; Clientes).
                    </MDTypography>
                </Card>
            </Grid>
        </Grid>
    );
}

PrestacaoContasUmc.propTypes = {
    cliente: PropTypes.object.isRequired,
    seletorCliente: PropTypes.node.isRequired,
};
